import { useState, useEffect, useCallback, useRef } from 'react';
import type { BannerSlide } from '@/shared/types/api.types';
import { cn } from '@/lib/utils';

interface HeroBannerProps {
  slides: BannerSlide[];
  autoplayDelay?: number;
}

export function HeroBanner({ slides, autoplayDelay = 5000 }: HeroBannerProps): React.ReactElement {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const showSlide = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);

      let newIndex = index;
      if (index >= slides.length) {
        newIndex = 0;
      } else if (index < 0) {
        newIndex = slides.length - 1;
      }

      setCurrentSlide(newIndex);
      setTimeout(() => setIsTransitioning(false), 1000);
    },
    [isTransitioning, slides.length]
  );

  const nextSlide = useCallback(() => showSlide(currentSlide + 1), [currentSlide, showSlide]);
  const prevSlide = useCallback(() => showSlide(currentSlide - 1), [currentSlide, showSlide]);

  const startAutoplay = useCallback(() => {
    autoplayRef.current = setInterval(nextSlide, autoplayDelay);
  }, [autoplayDelay, nextSlide]);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }
  }, []);

  const resetAutoplay = useCallback(() => {
    stopAutoplay();
    startAutoplay();
  }, [startAutoplay, stopAutoplay]);

  useEffect(() => {
    startAutoplay();
    return stopAutoplay;
  }, [startAutoplay, stopAutoplay]);

  function handleTouchStart(event: React.TouchEvent): void {
    touchStartX.current = event.changedTouches[0]?.screenX ?? 0;
  }

  function handleTouchEnd(event: React.TouchEvent): void {
    touchEndX.current = event.changedTouches[0]?.screenX ?? 0;
    const diff = touchEndX.current - touchStartX.current;
    if (diff < -50) {
      nextSlide();
    } else if (diff > 50) {
      prevSlide();
    }
  }

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'ArrowLeft') {
        prevSlide();
        resetAutoplay();
      } else if (event.key === 'ArrowRight') {
        nextSlide();
        resetAutoplay();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, resetAutoplay]);

  return (
    <section className="bg-muted relative w-full overflow-hidden">
      <div
        className="relative h-[45vh] w-full"
        onMouseEnter={stopAutoplay}
        onMouseLeave={startAutoplay}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {slides.map((slide, index) => (
          <div
            key={index}
            className={cn(
              'absolute inset-0 h-full w-full transition-opacity duration-1000',
              index === currentSlide ? 'z-10 opacity-100' : 'z-0 opacity-0'
            )}
          >
            <img
              src={slide.image}
              alt={slide.alt}
              className="absolute inset-0 h-full w-full object-cover"
            />

            <div className="absolute inset-0 bg-linear-to-r from-black/70 via-black/50 to-transparent" />

            {(slide.title || slide.description) && (
              <div className="absolute inset-0 flex items-center">
                <div className="container mx-auto px-4 md:px-8 lg:px-16">
                  <div className="max-w-2xl space-y-6 text-white">
                    {slide.title && (
                      <h2 className="animate-fade-in-up text-3xl leading-tight font-bold md:text-4xl lg:text-5xl">
                        {slide.title}
                      </h2>
                    )}
                    {slide.description && (
                      <p className="animate-fade-in-up animation-delay-200 text-base text-white/90 md:text-lg lg:text-xl">
                        {slide.description}
                      </p>
                    )}
                    {slide.link && slide.linkText && (
                      <a
                        href={slide.link}
                        className="animate-fade-in-up animation-delay-400 bg-primary text-primary-foreground shadow-medium hover:bg-primary/90 hover:shadow-strong inline-flex transform items-center rounded-lg px-8 py-4 font-semibold transition-all duration-300 hover:scale-105"
                      >
                        {slide.linkText}
                        <svg
                          className="ml-2 h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                          />
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        onClick={() => {
          prevSlide();
          resetAutoplay();
        }}
        className="group absolute top-1/2 left-2 z-20 -translate-y-1/2 transform rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-all duration-300 hover:scale-110 hover:bg-white/40 md:left-4 md:p-3"
        aria-label="Slide anterior"
      >
        <svg
          className="h-4 w-4 transition-transform group-hover:-translate-x-1 md:h-5 md:w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <button
        onClick={() => {
          nextSlide();
          resetAutoplay();
        }}
        className="group absolute top-1/2 right-2 z-20 -translate-y-1/2 transform rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-all duration-300 hover:scale-110 hover:bg-white/40 md:right-4 md:p-3"
        aria-label="Slide siguiente"
      >
        <svg
          className="h-4 w-4 transition-transform group-hover:translate-x-1 md:h-5 md:w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 gap-2 md:bottom-6">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              showSlide(index);
              resetAutoplay();
            }}
            className={cn(
              'h-2 rounded-full transition-all duration-300 md:h-2.5',
              index === currentSlide
                ? 'w-6 bg-white md:w-8'
                : 'w-2 bg-white/50 hover:bg-white/80 md:w-2.5'
            )}
            aria-label={`Ir a slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
